import { motion } from 'motion/react';
import { useRef, useState, useEffect } from 'react';
import { useIsInView } from '../../hooks/useIsInView';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { ArrowRight } from 'lucide-react';
import { isFinnishPage } from '../../lib/blogPaths';
import { getWpImageSrc } from '../../lib/wpMedia';

const MOBILE_BREAKPOINT_PX = 768;

// Images: put files in public/partnerships/ named by slug (e.g. mehilainen.jpg).
// Fallback images in public/partnerships/ when WordPress image is not available.
const img = (slug) => `/partnerships/${slug}.jpg`;

// Case study links: front page uses language (FI → /tyomme/[slug], EN → /work/[slug]). With cases from API, linkBase is passed by parent.
const featuredCases = [
  {
    client: 'Mehiläinen',
    slug: 'mehilainen',
    caseSlug: 'mehilainen',
    title:
      'Powering Customer-Centric Digital Healthcare Development Through Partnership',
    titleFi: 'Kumppanuudesta voimaa asiakaslähtöisen digitaalisen terveydenhuollon kehittämiseen',
    subtitle: 'Healthcare Digital Transformation',
    description:
      'Comprehensive digital health platform serving over 1M patients with seamless appointment booking and telemedicine capabilities.',
    result: 'Serving 1M+ patients with seamless digital healthcare',
    brandColor: '#00A651',
  },
  {
    client: 'Visma Sirius',
    slug: 'visma',
    caseSlug: 'visma-sirius',
    title: 'Boosting Customer Focus with a New Mobile Application',
    titleFi: 'Asiakaslähtöisyyteen potkua uuden mobiilisovelluksen myötä',
    subtitle: 'Mobile App Development',
    description:
      'Award-winning mobile app that revolutionized customer engagement with innovative UX and real-time data synchronization.',
    result: '4.8★ App Store rating with 50K+ active users',
    brandColor: '#FF5B00',
  },
  {
    client: 'Smartum',
    slug: 'smartum',
    caseSlug: 'smartum',
    title: 'Customer Understanding Drives Digital Service Development',
    titleFi: 'Asiakasymmärryksestä digitaalisen palvelukehityksen perusta',
    subtitle: 'HealthTech • User Research • Design',
    description:
      'User-centered design and development for digital wellness and benefit services.',
    result: 'Improved engagement and service delivery',
    brandColor: '#0066B3',
  },
];

/**
 * @param {Array<{ client: string, title: string, slug: string, imageUrl?: string|null }>} [cases] - When provided (e.g. from WP Work page), use these for cards instead of static featuredCases.
 */
export function FeaturedWorkSection({
  /** WP image URLs for featured cases: { mehilainen?, visma?, smartum? }. When set, overrides static image for that slug. */
  featuredCaseImageUrls = null,
  /** @deprecated Use featuredCaseImageUrls.smartum instead. */
  smartumCardImageUrl = null,
  title,
  subtitle,
  limit,
  cases: casesProp,
  /** Link text for "View all case studies" (e.g. from ACF). When limit is set, fallback is "View all case studies". */
  viewAllText,
  /** When true, hide the "View all case studies" link (e.g. on the Work page). */
  hideViewAll = false,
  /** When cases are from API: 'work' → /work/[slug], 'tyomme' → /tyomme/[slug] (Finnish Meistä). */
  caseStudyLinkBase = 'work',
  /** When set, "View all" link goes here (e.g. /tyomme for Finnish). Defaults to /work. */
  viewAllHref,
}) {
  const imageUrls = featuredCaseImageUrls || {};
  const router = useRouter();
  const ref = useRef(null);
  const isInView = useIsInView(ref, { once: true, margin: '-100px' });
  const isFinnish = isFinnishPage(router?.pathname) || router?.asPath === '/meista';
  const readCaseStudyLabel = isFinnish ? 'Lue asiakastarina' : 'Read Case Study';
  const staticCases = limit ? featuredCases.slice(0, limit) : featuredCases;
  const cases = casesProp?.length > 0 ? casesProp : staticCases;
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`);
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const displayCases = isMobile && !casesProp?.length ? cases.slice(0, 2) : cases;
  const sectionId = title ? undefined : 'featured-work';
  const useWorkLinks = casesProp?.length > 0;
  const linkBase = caseStudyLinkBase === 'tyomme' ? '/tyomme' : '/work';
  const defaultTitle = isFinnish ? 'Valittuja kumppanuuksia' : "Trusted by Finland's Leading Enterprises";
  const defaultViewAllText = isFinnish ? 'Katso kaikki asiakastarinat' : (limit ? 'View all case studies' : 'View All Case Studies');
  const viewAllLink = viewAllHref ?? (isFinnish ? '/tyomme' : '/work');
  // Static cases (front page): FI → /tyomme/[caseSlug or slug], EN → /work/[caseSlug or slug]
  const getCardHref = (project) => {
    const slug = project.caseSlug ?? project.slug;
    return useWorkLinks
      ? `${linkBase}/${project.slug}`
      : isFinnish
        ? `/tyomme/${slug}`
        : `/work/${slug}`;
  };

  return (
    <section id={sectionId} ref={ref} className="section-rhythm bg-gray-50 [content-visibility:auto]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 min-w-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="heading-2 text-center mb-4 md:mb-6 px-4 md:px-0">
            {title ?? defaultTitle}
          </h2>
          {subtitle && (
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{subtitle}</p>
          )}
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-8 md:mb-10">
          {displayCases.map((project, index) => {
            const cardHref = getCardHref(project);
            const cardTitle = isFinnish && project.titleFi ? project.titleFi : project.title;
            const rawImageSrc =
              useWorkLinks && project.imageUrl
                ? project.imageUrl
                : imageUrls[project.slug] ?? (project.slug === 'smartum' && smartumCardImageUrl) ?? img(project.slug);
            const imageSrc = getWpImageSrc(rawImageSrc) || rawImageSrc;
            return (
              <motion.div
                key={project.id ?? project.slug ?? project.client ?? index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                className="case-study-card group relative bg-white rounded-2xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.06)] md:hover:shadow-[0_12px_40px_-10px_rgba(0,0,0,0.12)] transition-all duration-300 focus-within:ring-2 focus-within:ring-brand focus-within:ring-offset-2 focus-within:outline-none"
              >
                <Link href={cardHref} className="block cursor-pointer no-underline focus:outline-none touch-manipulation">
                  <div className="rounded-t-2xl h-44 md:h-56 bg-gray-900 relative overflow-hidden">
                    <Image
                      src={imageSrc}
                      alt={`${project.client} case study`}
                      fill
                      sizes="(max-width: 768px) 100vw, 33vw"
                      className="object-cover rounded-t-2xl md:group-hover:scale-105 transition-transform duration-300 delay-75"
                      unoptimized
                    />
                  </div>

                  <div className="p-5 md:p-6">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{project.client}</p>
                    <h3 className="text-base md:text-lg font-bold text-gray-900 mb-4 leading-snug">
                      {cardTitle}
                    </h3>
                    <span className="read-more-link text-brand font-medium inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                      {readCaseStudyLabel}
                      <ArrowRight className="w-5 h-5 shrink-0" aria-hidden />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {!hideViewAll && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center"
        >
          <Link
            href={viewAllLink}
            className="view-all-cta group inline-flex items-center gap-2 text-brand font-semibold text-lg hover:gap-3 transition-all focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 rounded touch-manipulation"
          >
            {viewAllText ?? defaultViewAllText}
            <ArrowRight className="w-5 h-5 shrink-0 transition-transform duration-200 group-hover:translate-x-1" aria-hidden />
          </Link>
        </motion.div>
        )}
      </div>
    </section>
  );
}

const caseShape = PropTypes.shape({
  client: PropTypes.string,
  title: PropTypes.string,
  slug: PropTypes.string,
  caseSlug: PropTypes.string,
  imageUrl: PropTypes.string,
  description: PropTypes.string,
  result: PropTypes.string,
  brandColor: PropTypes.string,
});

FeaturedWorkSection.propTypes = {
  featuredCaseImageUrls: PropTypes.shape({
    mehilainen: PropTypes.string,
    visma: PropTypes.string,
    smartum: PropTypes.string,
  }),
  smartumCardImageUrl: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  limit: PropTypes.number,
  cases: PropTypes.arrayOf(caseShape),
  viewAllText: PropTypes.string,
  hideViewAll: PropTypes.bool,
  caseStudyLinkBase: PropTypes.string,
  viewAllHref: PropTypes.string,
};

FeaturedWorkSection.defaultProps = {
  hideViewAll: false,
  caseStudyLinkBase: 'work',
};
